import { Link } from "react-router-dom";
import { ArrowRight, Clock, MessageCircle } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { GUIDE_LIST, GUIDES_HUB } from "@/data/guidePages";
import { breadcrumbSchema, faqSchema, SITE_URL, canonical } from "@/lib/seo";
import { REPORT_PROMISE, waInspectorHref } from "@/lib/business";

const HUB_FAQS = [
  { q: "Who are these guides for?", a: "For Dubai property buyers and owners — whether you are preparing for handover, inside your defect liability period, buying resale, or simply want to understand what a professional inspection covers before you book." },
  { q: "Are the guides written by inspectors?", a: "Yes. The guides are published by Fedari Inspection, a licensed UAE building inspection company (trade licence 1647070), and reflect how our inspections actually work — scope, reporting and timing." },
  { q: "Do the guides replace an inspection?", a: "No. They help you understand the process and what to look for, but a professional inspection adds tools, trained judgement and a documented photo-led report you can submit to a developer or seller." },
];

export default function GuidesHub() {
  const path = GUIDES_HUB;
  const title = "Snagging & Inspection Guides for Dubai | Fedari";
  const description =
    "Practical guides to property snagging in Dubai: what snagging is, snagging vs handover vs DLP, 2026 prices and a room-by-room checklist. Written by Fedari's inspection team.";

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Property snagging and inspection guides",
    itemListElement: GUIDE_LIST.map((g, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: g.title,
      url: `${SITE_URL}${g.href}`,
    })),
  };
  const jsonLd = [
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Guides", path }]),
    itemList,
    faqSchema(HUB_FAQS),
  ];
  const alternates = [
    { hreflang: "en-AE", href: canonical(path) },
    { hreflang: "ar-AE", href: canonical("/ar/guides") },
    { hreflang: "x-default", href: canonical(path) },
  ];

  return (
    <PageShell breadcrumbs={[{ name: "Guides", path }]}>
      {({ onQuote }) => (
        <>
          <Seo title={title} description={description} path={path} jsonLd={jsonLd} alternates={alternates} />

          {/* Hero */}
          <section className="wrap pt-6 pb-10 lg:pt-10 lg:pb-12">
            <span className="eyebrow">Guides · Dubai property inspection</span>
            <h1 className="text-[clamp(1.9rem,4vw,3rem)] leading-[1.07] tracking-[-0.03em] mt-3 mb-5 max-w-[24ch]">Snagging and inspection guides for Dubai property buyers</h1>
            <p className="text-[1.1rem] text-bodytext max-w-[64ch] mb-4">
              Understand the inspection before you book it. These guides explain what snagging covers, how handover and DLP inspections differ, what it costs in 2026, and what a proper room-by-room check looks like — with a photo-led report {REPORT_PROMISE}.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-6">
              <button onClick={onQuote} data-testid="guides-quote-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                Get quote &amp; availability <ArrowRight size={17} />
              </button>
              <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" data-testid="guides-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
                <MessageCircle size={17} /> WhatsApp Fedari
              </a>
            </div>
          </section>

          {/* Guide grid */}
          <section className="wrap pb-14">
            <div className="grid sm:grid-cols-2 gap-4" data-testid="guide-grid">
              {GUIDE_LIST.map((g) => (
                <Link key={g.slug} to={g.href} data-testid={`guide-card-${g.slug}`}
                  className="group border border-line rounded-2xl p-6 bg-white hover:border-blue hover:shadow-sm transition-all flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-blue font-mono-jb uppercase tracking-wide">Guide</span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted"><Clock size={12} /> {g.readingMinutes} min read</span>
                  </div>
                  <h2 className="text-lg font-display mb-2 group-hover:text-blue transition-colors">{g.title}</h2>
                  <p className="text-sm text-bodytext mb-4">{g.excerpt}</p>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-blue">
                    Read the guide <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="wrap pb-16">
            <h2 className="text-2xl mb-5">About these guides — FAQs</h2>
            <div className="space-y-4 max-w-[80ch]">
              {HUB_FAQS.map((f, i) => (
                <div key={i} className="border border-line rounded-xl p-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-bodytext text-[0.95rem]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
